import type { InputMaybe } from '@openx/types/gqlTypes';
import isEmpty from 'lodash/isEmpty';

// Relation names where empty objects are semantically meaningful in GraphQL filters.
// E.g., `{ countries: {} }` in `_not: { countries: {} }` means "no countries in relation".
const RELATION_KEYS = new Set(['countries', 'regions', 'provider', 'segment', 'account']);

// GraphQL filter operators
export type EqFilter<T> = { _eq: T };
export type NeqFilter<T> = { _neq: T };
export type InFilter<T> = { _in: readonly T[] };
export type NinFilter<T> = { _nin: readonly T[] };
export type IsNullFilter = { _is_null: boolean };
export type ILikeFilter = { _ilike: string };
/** @knipignore */
export type GteFilter<T> = { _gte: T };
/** @knipignore */
export type LteFilter<T> = { _lte: T };
export type ContainsFilter<T> = { _contains: T };

// Combined range filter
export type RangeFilter = {
  _gte?: number;
  _lte?: number;
};

// All possible filter operators for a field
export type FieldFilter<T> =
  | EqFilter<T>
  | NeqFilter<T>
  | InFilter<T>
  | NinFilter<T>
  | IsNullFilter
  | ILikeFilter
  | RangeFilter
  | ContainsFilter<T>;

// Logical operators that match the structure of Segment_Bool_Exp
export type AndCondition = { _and: InputMaybe<WhereCondition[]> };
export type OrCondition = { _or: InputMaybe<WhereCondition[]> };

// A single field condition. We support nested objects so a field may map to
// either a FieldFilter (operators like {_gte,_ilike}) or another object of
// field conditions (for relations / nested fields).
export type FieldCondition = {
  [fieldName: string]: FieldFilter<unknown> | FieldCondition;
};

// Complete where condition type - designed to be compatible with Segment_Bool_Exp
// and to allow nested objects (e.g. lookalike_segment_seed_aggregate: { segment: { _gte: ... } }).
export type WhereCondition = FieldCondition | AndCondition | OrCondition | undefined;

/** Scalar types that can be used in GraphQL equality operations */
export type ScalarValue = string | number | boolean | null | undefined;

/** Equality filter, returns undefined if value is undefined */
export const eq = <T extends ScalarValue>(key: string, value?: T): Record<string, EqFilter<T>> | undefined =>
  value === undefined ? undefined : { [key]: { _eq: value } };

/** Inequality filter, returns undefined if value is undefined */
export const neq = <T extends ScalarValue>(key: string, value?: T): Record<string, NeqFilter<T>> | undefined =>
  value === undefined ? undefined : { [key]: { _neq: value } };

/** IN filter, returns undefined if values is empty or undefined */
export const in_ = <T extends ScalarValue>(
  key: string,
  values?: readonly T[]
): Record<string, InFilter<T>> | undefined => (values?.length ? { [key]: { _in: values } } : undefined);

export const not_in = <T extends ScalarValue>(
  key: string,
  values?: readonly T[]
): Record<string, NinFilter<T>> | undefined => (values?.length ? { [key]: { _nin: values } } : undefined);

/** AND-combiner, flattens single elements, returns undefined if no conditions */
export const and = (...conds: (WhereCondition | undefined)[]): WhereCondition | undefined => {
  const filtered = conds.filter((c): c is WhereCondition => Boolean(c));

  if (!filtered.length) return undefined;
  else if (filtered.length === 1) return filtered[0];

  return { _and: filtered };
};

export const is_null = (key: string, value?: boolean): Record<string, IsNullFilter> | undefined =>
  value === undefined ? undefined : { [key]: { _is_null: value } };

export const ilike = (key: string, value?: string): Record<string, ILikeFilter> | undefined =>
  value === undefined ? undefined : { [key]: { _ilike: `%${value}%` } };

/** OR-combiner, flattens single elements, returns undefined if no conditions */
export const or = (...conds: (WhereCondition | undefined)[]): WhereCondition | undefined => {
  const filtered = conds.filter((c): c is WhereCondition => Boolean(c));

  if (!filtered.length) return undefined;
  else if (filtered.length === 1) return filtered[0];

  return { _or: filtered };
};

/** Recursively strips out undefined objects/arrays to produce a clean where object */
export const clean = <T>(obj: T): T => {
  if (Array.isArray(obj)) {
    // Remove undefined or empty‐object elements
    const arr = obj
      .map(clean)
      .filter(item => item !== undefined && !(typeof item === 'object' && item !== null && isEmpty(item)));
    return arr as unknown as T;
  }

  if (obj && typeof obj === 'object') {
    const result = {};

    for (const [k, v] of Object.entries(obj)) {
      const c = clean(v);

      // Preserve empty objects in relation/query contexts; strip elsewhere
      const isEmptyObject = typeof c === 'object' && c !== null && isEmpty(c);
      const isRelationContext = RELATION_KEYS.has(k);
      const shouldKeep = !(isEmptyObject && !isRelationContext);

      if (c !== undefined && shouldKeep) {
        result[k] = c;
      }
    }

    return result as T;
  }

  return obj;
};

/** Builds a {_gte,_lte} filter if min/max are provided */
export const buildRangeFilter = (
  field: string,
  range?: { min?: number; max?: number }
): Record<string, RangeFilter> | undefined => {
  const cond: RangeFilter = {};

  if (!range || (range.min == null && range.max == null)) return undefined;
  if (range.min != null) cond._gte = range.min;
  if (range.max != null) cond._lte = range.max;

  return { [field]: cond };
};
